import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { ShieldCheck, Activity, Compass, Droplets } from "lucide-react";

/* ════════════════════════════════════════════════════════════════
   SENTINEL SCORE STRIP (P1 Feb-2026)
   Four advisory composite scores rendered as a compact horizontal
   strip above the existing SentinelInsightsPanel. Each tile shows:
     - icon
     - label
     - score /100
     - band colour (green ≥85, amber 70-84, red <70)
   Data comes from GET /api/dashboard/sentinel-summary.
   ──────────────────────────────────────────────────────────────
   Constraints (locked by directive):
   - This is purely additive — never replace the insights panel.
   - Scores are advisory only — no ledger mutations.
   - Render even when the API returns null scores (placeholder).
   ════════════════════════════════════════════════════════════════ */

const TILES = [
  {
    key: "trust",
    label: "Trust Score",
    Icon: ShieldCheck,
    description: "Average ledger truth + override penalty.",
  },
  {
    key: "discipline",
    label: "Discipline Score",
    Icon: Activity,
    description: "Tax + reserve + free equity health.",
  },
  {
    key: "risk_horizon",
    label: "Risk Horizon Score",
    Icon: Compass,
    description: "Runway × diversification × credit headroom.",
  },
  {
    key: "liquidity_warning",
    label: "Liquidity Score",
    Icon: Droplets,
    description: "Near-term cash adequacy.",
  },
];

const BAND = (score) => {
  if (score == null) return { ring: "ring-slate-700/60", text: "text-slate-400", dot: "bg-slate-500", label: "n/a" };
  if (score >= 85) return { ring: "ring-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-400", label: "Healthy" };
  if (score >= 70) return { ring: "ring-amber-500/40", text: "text-amber-300", dot: "bg-amber-400", label: "Monitor" };
  return { ring: "ring-red-500/40", text: "text-red-300", dot: "bg-red-400", label: "Attention" };
};

function ScoreTile({ tile, score }) {
  const band = BAND(score);
  const Icon = tile.Icon;
  return (
    <div
      className={`relative bg-slate-900/40 rounded-xl border border-slate-800/50 ring-1 ${band.ring} p-4 flex flex-col gap-1`}
      data-testid={`sentinel-score-tile-${tile.key}`}
      data-score={score == null ? "" : String(score)}
      data-band={band.label.toLowerCase()}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${band.text}`} />
        <span className="text-[11px] uppercase tracking-wider text-slate-400">
          {tile.label}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className={`text-3xl font-mono font-bold ${band.text}`}
          data-testid={`sentinel-score-value-${tile.key}`}
        >
          {score == null ? "—" : score}
        </span>
        <span className="text-xs text-slate-500">/100</span>
        <span className={`ml-auto inline-flex items-center gap-1.5 text-[10px] ${band.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${band.dot}`} />
          {band.label}
        </span>
      </div>
      <p className="text-[10px] text-slate-500 leading-relaxed mt-1">
        {tile.description}
      </p>
    </div>
  );
}

export default function SentinelScoreStrip() {
  const [scores, setScores] = useState(null); // null=loading, {}=loaded
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardApi.sentinelScores();
        if (!cancelled) setScores(res?.data?.scores || {});
      } catch {
        if (!cancelled) {
          setScores({});
          setError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      className="mb-4"
      data-testid="sentinel-score-strip"
      aria-label="Sentinel composite scores"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs uppercase tracking-[0.18em] text-slate-400 font-medium">
          Sentinel Score Strip
        </h2>
        <span className="text-[10px] text-slate-600">
          Advisory only · 10 vault aware
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TILES.map((t) => (
          <ScoreTile
            key={t.key}
            tile={t}
            score={scores == null ? null : (scores?.[t.key] ?? null)}
          />
        ))}
      </div>
      {error && (
        <p className="text-[10px] text-slate-600 mt-2" data-testid="sentinel-score-strip-error">
          Sentinel summary temporarily unavailable.
        </p>
      )}
    </section>
  );
}
